import { Box, Heading, Separator, Table } from "@chakra-ui/react-v3";
import { DiagnosticReport } from "@medplum/fhirtypes";
import DOMPurify from "dompurify";
import { SimplePdfViewerV2 } from "../../shared-components/simple-pdf-viewer-v2";
import {
  cleanBase64String,
  getBase64PdfOrImageType,
  isImageType,
  isPdfType,
} from "../../shared-logic/base64-utils";

type NoteDisplayProps = {
  resource: unknown;
};

/**
 * Component that displays notes from DiagnosticReport resources.
 * Extracts notes from the presentedForm field and renders them safely.
 */
export function NoteDisplay({ resource }: NoteDisplayProps) {
  if (!resource || typeof resource !== "object") {
    return null;
  }

  const resourceObj = resource as Record<string, unknown>;
  const resourceType = resourceObj.resourceType as string;

  // Only display notes for DiagnosticReport resources
  if (resourceType !== "DiagnosticReport") {
    return null;
  }

  const diagnosticReport = resource as DiagnosticReport;
  const cleanNotes = getCleanNotesFromReport(diagnosticReport);

  if (!cleanNotes || cleanNotes.length === 0) {
    return null;
  }

  return (
    <Box>
      <Heading size="md" mb={4} color="fg.emphasized">
        Note
      </Heading>
      <Box p={4} borderWidth="1px" borderColor="border.emphasized" borderRadius="md" bg="bg.subtle">
        {cleanNotes.map((cleanNote, index) => (
          <Box key={index}>
            <Box whiteSpace="pre-line">
              <NoteContent cleanNote={cleanNote} />
            </Box>
            {index < cleanNotes.length - 1 && <Separator borderColor="border.emphasized" my={4} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Extracts and decodes notes from DiagnosticReport presentedForm field.
 * Returns an array of cleaned note strings.
 */
function getCleanNotesFromReport(report: DiagnosticReport): string[] {
  const presentedForms = report.presentedForm || [];
  return presentedForms
    .map(form => {
      if (!form.data) return "";

      const cleanData = cleanBase64String(form.data);
      if (!cleanData) return "";

      const fileType = getBase64PdfOrImageType(cleanData);

      // Keep these file types encoded, handle later in the component
      if (isImageType(fileType) || isPdfType(fileType)) {
        return cleanData;
      }

      // Decode and clean text/HTML content
      try {
        return cleanUpNote(atob(cleanData));
      } catch (error) {
        console.error("Failed to decode presentedForm data:", error);
        return "";
      }
    })
    .filter(note => note.trim().length > 0);
}

/**
 * Cleans up note text by removing unwanted XML tags and artifacts.
 */
function cleanUpNote(note: string): string {
  return note
    .trim()
    .replace(/<ID>.*?<\/ID>/g, "")
    .replace(/Â/g, " ")
    .replace(/<styleCode>.*?<\/styleCode>/g, "")
    .replace(/<colspan>.*?<\/colspan>/g, "")
    .replace(/<script>.*?<\/script>/g, "");
}

/**
 * Checks if the note contains table tags.
 */
function isHasTableTag(cleanNote: string): boolean {
  return cleanNote.includes("<table") && cleanNote.includes("</table>");
}

/**
 * Parses XML content from the note.
 */
function parseXML(cleanNote: string): Document {
  const parser = new DOMParser();
  return parser.parseFromString(cleanNote, "application/xml");
}

/**
 * Handles HTML parsing and error checking.
 */
function handleHtml(parsedNote: string | null, originalNote: string): string {
  if (parsedNote?.includes("This page contains the following errors")) {
    return originalNote;
  }
  return parsedNote || originalNote;
}

/**
 * Sanitizes HTML content for safe rendering.
 */
function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html);
}

/**
 * Component for rendering notes that contain table data.
 */
function NoteAsTable({
  xmlDocument,
  sanitizedNote,
}: {
  xmlDocument: Document;
  sanitizedNote: string;
}) {
  const captionElement = xmlDocument.querySelector("caption");
  const tableElement = xmlDocument.querySelector("table");

  if (!tableElement) {
    return <div dangerouslySetInnerHTML={{ __html: sanitizedNote }} />;
  }

  const thead = tableElement.querySelector("thead");
  const tbody = tableElement.querySelector("tbody");
  const tfoot = tableElement.querySelector("tfoot");

  if (!thead && !tbody) {
    return <div dangerouslySetInnerHTML={{ __html: sanitizedNote }} />;
  }

  return (
    <Box
      overflowX="scroll"
      css={{
        "&::-webkit-scrollbar": {
          WebkitAppearance: "none",
          height: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: "4px",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      {captionElement && (
        <Heading as="h4" size="sm" paddingX={4} textAlign="left" my={4}>
          {captionElement.textContent?.trim()}
        </Heading>
      )}
      <Table.Root variant="line" size="sm" width="100%">
        {thead && (
          <Table.Header>
            {Array.from(thead.querySelectorAll("tr")).map((row, rowIndex) => (
              <Table.Row key={`thead-${rowIndex}`}>
                {Array.from(row.children).map((cell, cellIndex) => (
                  <Table.ColumnHeader
                    key={`thead-${rowIndex}-${cellIndex}`}
                    p={2}
                    fontWeight="bold"
                  >
                    {cell.textContent?.trim()}
                  </Table.ColumnHeader>
                ))}
              </Table.Row>
            ))}
          </Table.Header>
        )}
        {tbody && (
          <Table.Body>
            {Array.from(tbody.querySelectorAll("tr")).map((row, rowIndex) => (
              <Table.Row key={`tbody-${rowIndex}`}>
                {Array.from(row.children).map((cell, cellIndex) => {
                  const isHeader = cell.tagName.toLowerCase() === "th";
                  return isHeader ? (
                    <Table.ColumnHeader
                      key={`tbody-${rowIndex}-${cellIndex}`}
                      p={2}
                      fontWeight="bold"
                    >
                      {cell.textContent?.trim()}
                    </Table.ColumnHeader>
                  ) : (
                    <Table.Cell key={`tbody-${rowIndex}-${cellIndex}`} p={2}>
                      {cell.textContent?.trim()}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Body>
        )}
        {tfoot && (
          <Table.Footer>
            {Array.from(tfoot.querySelectorAll("tr")).map((row, rowIndex) => (
              <Table.Row key={`tfoot-${rowIndex}`}>
                {Array.from(row.children).map((cell, cellIndex) => (
                  <Table.ColumnHeader
                    key={`tfoot-${rowIndex}-${cellIndex}`}
                    p={2}
                    fontWeight="bold"
                  >
                    {cell.textContent?.trim()}
                  </Table.ColumnHeader>
                ))}
              </Table.Row>
            ))}
          </Table.Footer>
        )}
      </Table.Root>
    </Box>
  );
}

function NoteContent({ cleanNote }: { cleanNote: string }) {
  // Detect the file type from base64 data
  const detectedType = getBase64PdfOrImageType(cleanNote);
  const isImage = isImageType(detectedType);
  const isPdf = isPdfType(detectedType);
  const isText = !isPdf && !isImage;

  // Only parse HTML/XML for text content
  const isNoteAsTable = isText && isHasTableTag(cleanNote);
  const xmlDocument = isText ? parseXML(cleanNote) : null;
  const parsedNote = xmlDocument?.documentElement?.textContent ?? null;
  const sanitizedNote = isText ? sanitizeHtml(handleHtml(parsedNote, cleanNote)) : "";

  if (isPdf) {
    return <SimplePdfViewerV2 base64Content={cleanNote} />;
  }

  if (isImage) {
    return (
      <img
        src={`data:${detectedType};base64,${cleanNote}`}
        alt={`Medical report image - ${detectedType}`}
        style={{ maxWidth: "100%", height: "auto" }}
      />
    );
  }

  if (isNoteAsTable && xmlDocument) {
    return (
      <NoteAsTable
        // NOTE: Passing the non-sanitized note as Document for tables
        // to ensure proper rendering. We do strip all <script> tags, so should be safe.
        xmlDocument={xmlDocument}
        sanitizedNote={sanitizedNote}
      />
    );
  }

  return <div dangerouslySetInnerHTML={{ __html: sanitizedNote }} />;
}
